/**
 * @author: Yaochao
 * @date: 2021-07-02 14:08
 * @description：表格组件，封装表格样式和基本逻辑
 */

import React, {useReducer, useCallback, useEffect, useState, forwardRef, useImperativeHandle} from "react";
import {Table} from 'antd';

/**
 * 对Antd-Table封装
 * @param owncolumns 必选参数，Function类型，用于返回antd-table的colums，接收的参数是Function，用于刷新表格数据
 * @param queryAction 必选参数，用于列表数据获取
 * @param params 可选参数，请求附加参数
 * @param selected 是否可选中的
 * @param className 自定义的className
 * @param baseProps antd-table的props
 * @constructor
 */
const ZkTable = forwardRef(({currentPageNum:currentPageNum,className: className, owncolumns: owncolumns, queryAction: queryAction, onChange: onChange, params: params, selected: selected, selectedRowKeys: pSelectedRowKeys, getCheckboxProps: getCheckboxProps, onSelect: onSelect, hideDefaultSelections: hideDefaultSelections, baseProps: baseProps}, ref) => {
    const SelectType = {
        All: 'all',
        CurrentPage: 'CurrentPage',
        None: 'None'
    };
    const [selectedRowKeys, setSelectedRowKeys] = useState(pSelectedRowKeys ? pSelectedRowKeys : []);
    const [selectType, setSelectType] = useState(SelectType.CurrentPage);
    const [temParams, setTemParams] = useState({...params});
    //对外暴露刷新方法
    useImperativeHandle(ref, () => ({
        refresh: getData,
        deselectAll: deselectAll
    }));

    //初始化state参数
    const initialState = {
        loading: false,//数据是否在加载，用户控制加载图标的显示
        pagination: {
            current: currentPageNum ? currentPageNum : 1,//分页当前页
            pageSize: 10,//分页大小
            total: 0,//总数据量
            hideOnSinglePage: true,//只有一页时是否隐藏分页器
            showQuickJumper: true,//快速跳转到某页
            showSizeChanger: false
        },
        dataSource: [] // 数据源
    };

    const ActionType = {
        TOGGLE_LOADING: 'TOGGLE_LOADING',
        SET_PAGINATION: 'SET_PAGINATION', //改变数据源
        SET_DATA_SOURCE: 'SET_DATA_SOURCE',//改变分页数据
    };

    const reducer = (state, action) => {
        const {payload} = action;
        switch (action.type) {
            case ActionType.TOGGLE_LOADING:
                return {...state, loading: !state.loading};
            case ActionType.SET_PAGINATION:
                return {...state, pagination: payload.pagination};
            case ActionType.SET_DATA_SOURCE:
                return {...state, dataSource: payload.dataSource};
            default:
                return state
        }
    };
    const [state, dispatch] = useReducer(reducer, initialState);
    //对表格数据做缓存
    const getDataWrap = useCallback(
        getData,
        [JSON.stringify(params), state.pagination.current]
    );

    //缓存条件变化触发重新拉取表格数据
    useEffect(() => {
        resetPagination();
        getDataWrap()
    }, [getDataWrap]);

    useEffect(() => {
        setSelectedRowKeys(pSelectedRowKeys);
    }, [pSelectedRowKeys]);

    /**
     * 参数变化是重置翻页
     */
    function resetPagination() {
        if (params && JSON.stringify(temParams) != JSON.stringify(params)) {
            state.pagination.current = 1;
            state.pagination.total = 0;
            setTemParams(JSON.parse(JSON.stringify(params)))
        }
    }

    /**
     * 获取表格数据
     * @returns {Promise<void>}
     */
    async function getData() {
        deselectAll();
        dispatch({
            type: ActionType.TOGGLE_LOADING
        });
        // 分页字段名称转换
        const {current: pageNum, pageSize: pageSize} = state.pagination;
        let res = await queryAction(params, pageNum, pageSize).catch(error => {
            //异常处理
            dispatch({type: ActionType.TOGGLE_LOADING});
            return {}
        });
        // 关闭loading
        dispatch({
            type: ActionType.TOGGLE_LOADING
        });

        if (res) {
            const {pagInfo, list} = res;
            if (pagInfo && list) {
                wraperDataKey(list);
                dispatch({
                    type: ActionType.SET_PAGINATION,
                    payload: {
                        pagination: {...state.pagination, total: pagInfo.total, pageSize: pagInfo.pageSize}
                    }
                });
                // 回填list数据
                dispatch({
                    type: ActionType.SET_DATA_SOURCE,
                    payload: {
                        dataSource: list
                    }
                });
                if (onChange) {
                    const {pageNum, pageSize, total} = pagInfo;
                    onChange({
                        pagination: {
                            current: pageNum,//分页当前页
                            pageSize: pageSize,//分页大小
                            total: total,//总数据量
                        },
                        data: list
                    })
                }
            }
        }
    }

    //选中的时候需要给数据加key
    function wraperDataKey(data) {
        for (let i = 0; i < data.length; i++) {
            data[i].key = i;
        }
    }

    function handleTableChange(payload) {
        if (payload) {
            const {current} = payload;
            dispatch({
                type: ActionType.SET_PAGINATION,
                payload: {
                    pagination: {
                        ...state.pagination,
                        current
                    }
                }
            })
        }
    }

    function onSelectChange(selectedRowKeys) {
        processSelect(selectedRowKeys);
    }

    //全部选中
    function selectAll() {
        selectTypeChange(SelectType.All);
    }

    function selectCurrentPage() {
        selectTypeChange(SelectType.CurrentPage);
    }

    function selectTypeChange(type) {
        let pageSize = state.pagination.pageSize;
        let selectedRowKeys = [];
        for (let start = 0; start < pageSize; start++) {
            selectedRowKeys.push(start);
        }
        setSelectType(type);
        processSelect(selectedRowKeys, type);
    }

    function deselectAll() {
        setSelectedRowKeys([])
        setSelectType(SelectType.CurrentPage)
        if (onSelect) {
            onSelect(SelectType.CurrentPage, [])
        }
    }

    function processSelect(selectedRowKeys, type) {
        if (!type) {
            type = selectType;
        }
        if (type == SelectType.All) {
            let pageSize = state.pagination.pageSize;
            let keys = [];
            for (let i = 0; i < pageSize; i++) {
                keys.push(i);
            }
            setSelectedRowKeys(keys);
            //全选
            if (onSelect) {
                onSelect(SelectType.All);
            }
        } else if (type == SelectType.CurrentPage) {
            //当前页面
            setSelectedRowKeys(selectedRowKeys);
            if (onSelect) {
                let data = [];
                for (let i in selectedRowKeys) {
                    let selectRow = state.dataSource[selectedRowKeys[i]];
                    if (selectRow) {
                        data.push(selectRow);
                    }
                }
                onSelect(SelectType.CurrentPage, data);
            }
        }
    }

    const rowSelection = {
        selectedRowKeys,
        columnWidth: 46,
        onChange: onSelectChange,
        hideDefaultSelections: true,
        getCheckboxProps: getCheckboxProps,
        selections: hideDefaultSelections ? null : [
            {
                key: 'all',
                text: '选择全部',
                onSelect: selectAll,
            },
            {
                key: 'current',
                text: '选择当前页面',
                onSelect: selectCurrentPage,
            }
        ],
    };

    return (
        <Table
            rowSelection={selected ? rowSelection : null}
            className={selectType == SelectType.All ? `${className} qy-table selectAll` : `${className} qy-table`}
            columns={owncolumns(getData)}
            pagination={state.pagination}
            dataSource={state.dataSource}
            loading={state.loading}
            onChange={handleTableChange}
            {...baseProps}
        />
    )

});

export default ZkTable